import { gray } from '@ant-design/colors';
import { HomeOutlined } from '@ant-design/icons';
import { Breadcrumb, Tooltip } from 'antd';
import { Link, useLocation } from 'react-router-dom';
import { FunctionComponent, useMemo } from 'react';
import { capitalCase } from 'change-case';
import { useTenantBasepath } from 'packages/lib/auth/is-path';

const formatSegmentLabel = (segment: string): string =>
  capitalCase(segment.replace(/-/g, ' ').replace(/_/g, ' '));

// Jun-3-2026 Used Cursor Auto to create pathUpToSegment to all links in breadcrumbs take user to where they'd expect to go

const pathUpToSegment = (pathParts: string[], index: number): string =>
  `/${pathParts.slice(0, index + 1).join('/')}`;

const getBreadcrumbTo = (
  part: string,
  index: number,
  pathParts: string[]
): string => {
  const tasksIndex = pathParts.indexOf('tasks');
  const workflow = pathParts[tasksIndex + 1];

  if (tasksIndex < 0 || !workflow) {
    return pathUpToSegment(pathParts, index);
  }

  const tenantPrefix = pathParts.slice(0, tasksIndex);
  const tenantPath = tenantPrefix.length ? `/${tenantPrefix.join('/')}` : '';
  const workflowPath = `/${[...tenantPrefix, 'tasks', workflow].join('/')}`;
  const pipeline = pathParts[tasksIndex + 2];
  const taskState = pathParts[tasksIndex + 3];

  if (part === 'tasks') {
    return `${tenantPath}/tools`;
  }

  // /tasks/:workflow/:pipeline has no page and redirects to /tools
  if (part === pipeline && taskState) {
    return workflowPath;
  }

  return pathUpToSegment(pathParts, index);
};

export const Breadcrumbs: FunctionComponent<
  React.PropsWithChildren<unknown>
> = () => {
  const { pathname } = useLocation();
  const pathParts = pathname.split('/').slice(1).filter(Boolean);
  const basePath = useTenantBasepath();

  const breadcrumbItems = useMemo(() => {
    return [
      {
        key: 'home',
        title: (
          <Tooltip title="Go to tools portal">
            <Link
              to={`${basePath}/tools`}
              style={{
                color: gray[1],
              }}
            >
              <HomeOutlined style={{ marginRight: '0.25rem' }} />
            </Link>
          </Tooltip>
        ),
      },
      ...pathParts.map((part, index) => {
        const label = formatSegmentLabel(part);
        const isLast = index === pathParts.length - 1;

        return {
          key: `${part}-${index}`,
          title: isLast ? (
            <Tooltip title={label}>
              <span style={{ color: 'white' }}>{label}</span>
            </Tooltip>
          ) : (
            <Tooltip title={label}>
              <Link
                style={{ color: 'white' }}
                to={getBreadcrumbTo(part, index, pathParts)}
              >
                {label}
              </Link>
            </Tooltip>
          ),
        };
      }),
    ];
  }, [basePath, pathParts]);

  return (
    <div
      style={{
        marginLeft: '1.75rem',
        color: gray[2],
        fontSize: '1rem',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Breadcrumb
        items={breadcrumbItems}
        separator={
          <span style={{ color: gray[1], margin: '0 0.5rem' }}>/</span>
        }
      />
    </div>
  );
};
